import isEqual from 'react-fast-compare';

import {ClientSchemaField} from 'registry/field/FieldTable';
import {ComputedFieldType, FieldDefinition, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {massageFieldsForSubmit} from 'schemas/SchemaUtil';
import {isLinkFieldType} from 'tag-schema/util/TagSchemaUtil';
import APIUtil from 'util/APIUtil';

export function getFieldDefinition(field: SchemaField): FieldDefinition {
  if (field.isSnapshot) {
    return ComputedFieldType.SNAPSHOT;
  } else {
    return field.fieldType;
  }
}

export function getSnapshotFieldDisabledMessage(
  schema: Schema | null,
  initialField: SchemaField | ClientSchemaField
): string | undefined {
  let msg;
  if (schema == null) {
    msg = 'Not available during schema creation';
  } else if (
    !schema.fields.some((field) => isLinkFieldType(field.fieldType)) &&
    !APIUtil.isProcessOutputSchema(schema.id)
  ) {
    msg = 'Not available if there are no link fields';
  } else if (initialField.apiId != null && !initialField.isSnapshot) {
    msg = 'Cannot change existing field into computed field (snapshot)';
  }
  return msg;
}

function getNewOrUpdatedFields(
  oldFields: Array<SchemaField | ClientSchemaField>,
  newFields: Array<SchemaField | ClientSchemaField>
): Array<SchemaField | ClientSchemaField> {
  const sanitizedOldFields = massageFieldsForSubmit(oldFields);
  const sanitizedNewFields = massageFieldsForSubmit(newFields);
  return sanitizedNewFields.filter(
    (newField) => !sanitizedOldFields.some((oldField) => isEqual(oldField, newField))
  );
}

function getDeletedFields(
  oldFields: Array<SchemaField | ClientSchemaField>,
  newFields: Array<SchemaField | ClientSchemaField>
): Array<SchemaField | ClientSchemaField> {
  return oldFields.filter((oldField) => !newFields.some((newField) => oldField.apiId === newField.apiId));
}

function hasReorderedFields(
  oldFields: Array<SchemaField | ClientSchemaField>,
  newFields: Array<SchemaField | ClientSchemaField>
): boolean {
  const reorderedFields = newFields.filter((newField, newIndex) =>
    oldFields.some((oldField, oldIndex) => oldField.apiId === newField.apiId && newIndex !== oldIndex)
  );
  return reorderedFields.length > 0;
}

export function haveFieldsChanged(
  oldFields: Array<SchemaField | ClientSchemaField>,
  newFields: Array<SchemaField | ClientSchemaField>
): boolean {
  return (
    getNewOrUpdatedFields(oldFields, newFields).length > 0 ||
    getDeletedFields(oldFields, newFields).length > 0 ||
    hasReorderedFields(oldFields, newFields)
  );
}

export function getNewOrUpdatedSnapshotFields(
  oldFields: Array<SchemaField | ClientSchemaField>,
  newFields: Array<SchemaField | ClientSchemaField>
): Array<SchemaField | ClientSchemaField> {
  const newOrUpdateFields = getNewOrUpdatedFields(oldFields, newFields);
  return newOrUpdateFields.filter((field) => Boolean(field.isSnapshot));
}
